import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _diff from 'lodash/difference';
import _omit from 'lodash/omit';
import * as reservedSeatingSelectors from '../../../selectors/reserved-seating';
import { TICKET_FEE_SEPARATE } from '../../../reducers/ui';
import { getFeeDisplayOption } from '../../../selectors/ui';
import { createLoadingSelector } from '../../../selectors/helpers';
import { getMainEvent } from '../../../selectors/event';
import { addAlert, clearAlerts } from '../../../actions/ui';
import {
  cartSeats,
  setGaQuantity,
  removeSeat,
  setBestAvailableQuantities,
  setSectionLevelQuantities,
  setPyosLoading,
  PYOS_SELECTING_SEATS_REASON,
  PYOS_REMOVING_SEATS_REASON,
} from '../../../actions/reservedSeating';

import SeatsioChart from '../components/SeatsioChart';
import { getFetchedEventPriceLevels } from '../../../selectors/event';
import { withNavigation } from '@b/helpers/withNavigation';
import { isDynamicListing } from '@b/checkout/selectors/dynamic-listing';

const SUBMIT_CHANGES_DELAY = 1000;

const toleranceHeight = 55;

class SeatsioChartContainer extends Component {
  prevSelection = [];
  
  state = {
    pendingGaPriceLevelId: null,
    pendingGaSeatsioId: null,
    pendingGaQuantity: 0,
    selectGaQuantityTimeout: null,
    pendingObjectsToSelect: [],
    selectObjectsTimeout: null,
    currentSelection: [],
    chartHeight: 0,
  };

  componentDidUpdate(prevProps, prevState) {
    const filteredPrevSelection = prevState.currentSelection.map((seat) => _omit(seat, ['object.chart']));
    const filteredCurrentSelection = this.state.currentSelection.map((seat) => _omit(seat, ['object.chart']));
    if (JSON.stringify(filteredPrevSelection) !== JSON.stringify(filteredCurrentSelection)) {
      this.prevSelection = prevState.currentSelection;
    }
  }

  getChangeDateDivHeight = () => {
    const height = this.changeDateContainer?.offsetHeight;

    // 10px is change date div's margin
    return height ? height + 10 : 0;
  };

  onWindowResize = () => {
    if (!this.seatsioContainer) return;

    const cs = getComputedStyle(this.seatsioContainer);

    const paddingY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);

    const borderY = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);

    // Element width and height minus padding and border
    const elementHeight = this.seatsioContainer.offsetHeight - paddingY - borderY;

    const nextHeight = elementHeight - this.getChangeDateDivHeight();

    const diff = Math.abs(nextHeight - this.state.chartHeight);

    /**
     * tickets-wrap--seating-chart class has a height of 100vh - 55px
     * it seems like it triggers chart glitching exactly by 55px back and forth
     * this if statement prevents height updates
     * sometimes the diff exactly twice this value
     *
     * Note: this happens only in embed listings that has inline checkout(without button),
     * on top of that width of the screen has to be at least 950px
     * It probably happens because of IframeResizer that exist only in embed listings
     * which updates iframe's dimensions to content's dimensions
     * Unfortunately it still has some minor glitches, but at least it's not going
     * into infinite loop
     *
     * Note 2: I don't have explanation of this behaviour yet
     */
    if (diff <= toleranceHeight || diff === toleranceHeight * 2) {
      return;
    }

    this.setState({
      chartHeight: elementHeight - this.getChangeDateDivHeight(),
    });
  };

  componentDidMount() {
    this.seatsioContainer = document.getElementsByClassName('tickets__content__main')?.[0];
    this.changeDateContainer = document.getElementsByClassName('checkout__change-date')?.[0];
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  clearBestAvailablePicker = () => {
    this.props.setBestAvailableQuantities({});
    this.props.setSectionLevelQuantities({});
  };

  onBestAvailableSelectionFailed = () => {
    this.clearBestAvailablePicker();
    this.props.addAlert('checkout__unable_to_find_seats_that_met_your_criteria');
  };

  onBestAvailableSelected = () => {
    this.clearBestAvailablePicker();
  };

  onObjectSelected = (object, selectedTicketType) => {
    this.props.clearAlerts({ clearPermanent: true });
    const currentSelection = this.state.currentSelection;
    currentSelection.push({ object, selectedTicketType: selectedTicketType ?? null });
    this.setState({ currentSelection: currentSelection });
  };

  // Because we are using a non-standard approach of custom statuses to indicated a seat
  // being in a reservation, "seat deselect" events are fired directly after the status changes from
  // booked to in_res. This should be refactored eventually to get away from relying on these custom
  // statuses. Our actual "de-selecting" registers instead as a "select" event, hence the remove logic
  // appearing in the onObjectSelected. instead of in the onObjectDeselected methods. This also causes
  // some ugliness for the reserveOnSelect features - we attempt to re-reserve seats that we already have
  // when we try to free them up.
  onObjectDeselected = async (object, selectedTicketType) => {
    const currentSelection = this.state.currentSelection;
    const index = currentSelection.findIndex((i) => i.object.id === object.id);
    if (index > -1) {
      currentSelection.splice(index, 1);
    }
    this.setState({ currentSelection: currentSelection });

    if (object.objectType != 'GeneralAdmissionArea') {
      return;
    }
    this.setGaQuantity(object);
  };

  // another consequence of having custom statuses is that seats in the reservation are selectable. This
  // means that if there are tiered prices for each seat, the customer will have to choose a price level
  // when "deselecting" the seat. Instead
  onObjectClicked = async (object) => {
    if (object?.objectType != 'GeneralAdmissionArea') {
      const seat_status = object.status.split(':');
      if (
        Array.isArray(seat_status) &&
        seat_status.length > 1 &&
        seat_status[0] == 'in_res' &&
        seat_status[1] == this.props.reservationToken
      ) {
        this.props.setPyosLoading(true, PYOS_REMOVING_SEATS_REASON);
        if (object?.objectType == 'Table') {
          const seat = object.seats.length ? object.seats[0] : null;
          if (seat) {
            // removing one seat will remove the whole table
            seat.seatId = seat.id;
            await this.props.removeSeat(seat);
          }
        } else {
          await this.props.removeSeat(object);
        }
        this.props.setPyosLoading(false, null);
      }
    }
  };

  onSelectionValid = async () => {
    const currentSelection = this.state.currentSelection;
    while (currentSelection.length > 0) {
      const selected = currentSelection.shift();
      const { object, selectedTicketType } = selected;

      if (object.objectType === 'Table') {
        for (const seat of object.seats) {
          seat.category = object.category;
          seat.status = object.status;
          this.selectObject(seat, selectedTicketType);
        }
      } else if (object.objectType === 'GeneralAdmissionArea') {
        this.setGaQuantity(object);
      } else {
        this.selectObject(object, selectedTicketType);
      }
    }
  };

  onSelectionInvalid = async (violations) => {
    if (violations.includes("consecutiveSeats")){
      this.props.addAlert(this.props.forceConsecutiveSeatsMessage, 'error', true, {}, true);

      const diff = _diff(this.state.currentSelection, this.prevSelection);

      if (diff.length <= 1) {
        return;
      }

      const idsToRemove = [];
      
      diff.forEach((seat) => {
        idsToRemove.push(seat.object.id);
        seat.object.deselect();
      })

      const newSelection = this.state.currentSelection.filter((seat) => !idsToRemove.includes(seat.object.id));

      this.setState({ currentSelection: newSelection });
    }
  };

  setGaQuantity = (object) => {
    const level = Object.values(this.props.priceLevels).find((lvl) => lvl?.name == object?.category?.label);
    if (level) {
      if (this.state.selectGaQuantityTimeout) {
        clearTimeout(this.state.selectGaQuantityTimeout);
      }
      this.setState({
        pendingGaPriceLevelId: +level?.price_level_id,
        pendingGaSeatsioId: object?.id ?? null,
        pendingGaQuantity: +object?.numSelected,
        selectGaQuantityTimeout: setTimeout(() => {
          this.submitPendingGaQuantity();
        }, SUBMIT_CHANGES_DELAY),
      });
    }
  };

  submitPendingGaQuantity = async () => {
    const priceLevelId = this.state.pendingGaPriceLevelId;
    const quantity = this.state.pendingGaQuantity;
    const seatsioId = this.state.pendingGaSeatsioId;

    const currentQuantity = this.props.tickets.filter((ticket) =>
      this.props.usesLabels ? +ticket?.price_level_id === priceLevelId : ticket?.seat_info?.seatsio_ga_id === seatsioId,
    ).length;

    const loadingReason = quantity > currentQuantity ? PYOS_SELECTING_SEATS_REASON : PYOS_REMOVING_SEATS_REASON;

    this.setState({ pendingGaQuantity: 0, pendingGaPriceLevelId: null, pendingGaSeatsioId: null });
    this.props.setPyosLoading(true, loadingReason);
    await this.props.setGaQuantity(priceLevelId, quantity, seatsioId, this.props.usesLabels);
    this.props.setPyosLoading(false, null);
  };

  selectObject = (object, selectedTicketType) => {
    const toSelect = { ...object };
    if (+selectedTicketType?.pplId) {
      toSelect.sectionLevelId = +selectedTicketType.pplId;
    }
    if (this.state.selectObjectsTimeout) {
      clearTimeout(this.state.selectObjectsTimeout);
    }
    if (!this.objectIsPending(toSelect)) {
      this.addPendingObject(toSelect);
    } else {
      this.removePendingObject(toSelect);
    }
    this.setState({
      selectObjectsTimeout: setTimeout(() => {
        this.submitPendingObjects();
      }, SUBMIT_CHANGES_DELAY),
    });
  };

  objectIdentifier = () => {
    return this.props.usesLabels ? 'label' : 'uuid';
  };

  objectIsPending = (object) => {
    let result = false;
    this.state.pendingObjectsToSelect.forEach((pendingObject) => {
      if (
        object?.[this.objectIdentifier()] &&
        object[this.objectIdentifier()] == pendingObject?.[this.objectIdentifier()]
      ) {
        result = true;
      }
    });
    return result;
  };

  addPendingObject = (object) => {
    if (object?.[this.objectIdentifier()]) {
      this.setState((prevState) => ({
        pendingObjectsToSelect: [...prevState.pendingObjectsToSelect, object],
      }));
    }
  };

  removePendingObject = (object) => {
    this.setState({
      pendingObjectsToSelect: this.state.pendingObjectsToSelect.filter((pendingObject) => {
        return pendingObject[this.objectIdentifier()] !== object?.[this.objectIdentifier()];
      }),
    });
  };

  submitPendingObjects = async () => {
    const pendingObjectsToSelect = [...this.state.pendingObjectsToSelect];
    this.setState({ pendingObjectsToSelect: [] });
    if (pendingObjectsToSelect.length) {
      this.props.setPyosLoading(true, PYOS_SELECTING_SEATS_REASON);
      await this.props.cartSeats(pendingObjectsToSelect, this.props.usesLabels, this.props.navigate);
      this.props.setPyosLoading(false, null);
    }
  };

  disableUi = () => {
    return this.props.pyosLoading || this.props.reservationLoading;
  };

  /**
   * Front end best available selection flow - this should only happen when user requests best available tickets
   * on an event where USE_SERVER_SIDE_BEST_AVAILABLE is false.
   * Note: this is deprecated per seatsio
   */
  getSelectBestAvailableProp = () => {
    let result = null;
    if (!this.props.useServerSideBestAvailable && this.props.numberSelectedBestAvailableSeats > 0) {
      result = {
        clearSelection: false, // don't remove previously selected seats, just add more
      };
      if (this.props.bestAvailableSelectedCategoryKey) {
        result.category = this.props.bestAvailableSelectedCategoryKey;
      }

      if (Object.keys(this.props.sectionLevelQuantities).length) {
        const ticketTypes = this.getTicketTypesForBestAvailableProp();
        if (Object.keys(ticketTypes).length) {
          result.ticketTypes = ticketTypes;
        }
      }

      if (result.ticketTypes) {
        const discountTicketAmount = Object.values(result.ticketTypes).reduce((a, b) => a + b, 0);
        if (this.props.numberSelectedBestAvailableSeats > discountTicketAmount){
          result.ticketTypes.Regular = this.props.numberSelectedBestAvailableSeats - discountTicketAmount;
        }
      }else{
        if (this.props.is_ga || !this.props.hasDiscountLevel) {
          result.number = this.props.numberSelectedBestAvailableSeats ;
        } else {
          result.ticketTypes = {Regular:this.props.numberSelectedBestAvailableSeats};
        }
      }
    }
    return result;
  };

  getTicketTypesForBestAvailableProp = () => {
    const ticketTypes = {};
    Object.keys(this.props.sectionLevelQuantities).forEach((sectionLevelId) => {
      this.props.sectionPriceLevels.forEach((sectionPriceLevel) => {
        if (+sectionPriceLevel?.id === +sectionLevelId) {
          ticketTypes[sectionPriceLevel.name] = this.props.sectionLevelQuantities[sectionLevelId];
        }
      });
    });
    return ticketTypes;
  };

  onChartRendered = (chart) => {
    this.setState({ chartHeight: chart.initialContainerDimensions.height - this.getChangeDateDivHeight() });
    window.addEventListener('resize', this.onWindowResize);
  };

  render() {
    const props = {
      onBestAvailableSelectionFailed: this.onBestAvailableSelectionFailed,
      onBestAvailableSelected: this.onBestAvailableSelected,
      onObjectSelected: this.onObjectSelected,
      onObjectDeselected: this.onObjectDeselected,
      onObjectClicked: this.onObjectClicked,
      selectBestAvailable: this.getSelectBestAvailableProp(),
      onSelectionValid: this.onSelectionValid,
      onSelectionInvalid: this.onSelectionInvalid,
      onChartRendered: this.onChartRendered,
      ...this.props,
    };

    const loadingStyles = this.disableUi() ? { opacity: '.6', pointerEvents: 'none' } : {};
    const style = {
      ...loadingStyles,
      height: this.state.chartHeight || '100%',
    };

    const mainClassName = !this.props?.isDynamic
      ? 'seating-chart__chart-container'
      : 'seating-chart__chart-container--aspect-ratio';

    return (
      <div className={mainClassName} style={style}>
        {this.props.pyosLoading && this.props.pyosLoadingReason && (
          <div className="seating-chart__loading-reason">
            <p>{this.props.pyosLoadingReason}</p>
          </div>
        )}
        <SeatsioChart {...props} />
      </div>
    );
  }
}

const reservationLoading = createLoadingSelector(['RESERVATION_FETCH', 'RESERVATION_DELETE']);
const mapStateToProps = (state) => {
  return {
    tickets: state.tickets.tickets,
    pyosLoading: state.reservedSeating.pyosLoading,
    pyosLoadingReason: state.reservedSeating.pyosLoadingReason,
    reservationLoading: reservationLoading(state),
    priceLevels: getFetchedEventPriceLevels(state),
    publicKey: reservedSeatingSelectors.rsPublicKey(state),
    seatsioEventId: reservedSeatingSelectors.rsEventId(state),
    sectionPriceLevels: reservedSeatingSelectors.getSectionPriceLevels(state),
    sectionLevelQuantities: state.reservedSeating.sectionLevelQuantities,
    sectionFeeMap: reservedSeatingSelectors.getSectionFeeMap(state),
    hideBestAvailable: reservedSeatingSelectors.hideBestAvailable(state),
    feeVerbiage: getMainEvent(state)?.event?.fee_verbiage,

    //cms setting
    discloseFee: getFeeDisplayOption(state) == TICKET_FEE_SEPARATE,
    purchaseLimit: reservedSeatingSelectors.purchaseLimit(state),
    incrementBy: reservedSeatingSelectors.incrementBy(state),
    defaultPriceLevelName: reservedSeatingSelectors.defaultPriceLevelName(state),
    hidePremierePriceDiscount: reservedSeatingSelectors.hidePremierePriceDiscount(state),
    onlyShowBestAvailable: reservedSeatingSelectors.onlyShowBestAvailable(state),
    allowOrphanSeats: reservedSeatingSelectors.allowOrphanSeats(state),
    orphanSeatMessage: reservedSeatingSelectors.orphanSeatMessage(state),
    hasTicketsInReservation: !!state.tickets.tickets.length,
    reservationToken: state.reservation.token,
    priceLevelSelectString: 'Select a Ticket Type',
    locale: state.intl.locale,
    useServerSideBestAvailable: reservedSeatingSelectors.useServerSideBestAvailable(state),
    numberSelectedBestAvailableSeats: reservedSeatingSelectors.numberSelectedBestAvailableSeats(state),
    bestAvailableSelectedCategoryKey: reservedSeatingSelectors.bestAvailableSelectedCategoryKey(state),
    usesLabels: reservedSeatingSelectors.usesLabels(state),
    is_ga: reservedSeatingSelectors.bestAvailableSelectedIsGA(state),
    hasDiscountLevel: reservedSeatingSelectors.hasDiscountLevel(state),
    forceConsecutiveSeats: reservedSeatingSelectors.forceConsecutiveSeats(state),
    forceConsecutiveSeatsMessage: reservedSeatingSelectors.forceConsecutiveSeatsMessage(state),
    isDynamic: isDynamicListing(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      cartSeats,
      setGaQuantity,
      removeSeat,
      addAlert,
      setBestAvailableQuantities,
      setSectionLevelQuantities,
      setPyosLoading,
      clearAlerts,
    },
    dispatch,
  );
};

export default withNavigation(connect(mapStateToProps, mapDispatchToProps)(SeatsioChartContainer));
